import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCloudArrowUp, 
    faTimes, 
    faSpinner, 
    faCheckCircle,
    faImage,
    faVideo,
    faMicrophone,
    faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { sendToMediaCentre } from '../services/mediaService';
import './ModalBulkPublishToMediaCentre.css';

const ModalBulkPublishToMediaCentre = ({ isOpen, onClose, onSuccess, selectedItems, choiceFields }) => {
    const [formData, setFormData] = useState({
        topicName: '',
        series: [],
        productService: '',
        theme: '',
        entity: 'IF'
    });
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [publishProgress, setPublishProgress] = useState({
        current: 0,
        total: 0,
        currentItem: ''
    });

    // Custom field add states
    const [isAddingNewTopic, setIsAddingNewTopic] = useState(false);
    const [customTopicName, setCustomTopicName] = useState('');
    const [isAddingNewProduct, setIsAddingNewProduct] = useState(false);
    const [customProductName, setCustomProductName] = useState('');
    const [isAddingNewTheme, setIsAddingNewTheme] = useState(false);
    const [customThemeName, setCustomThemeName] = useState('');
    const [isAddingNewEntity, setIsAddingNewEntity] = useState(false);
    const [customEntityName, setCustomEntityName] = useState('');

    // Reset form when modal opens
    useEffect(() => {
        if (isOpen) {
            setFormData({
                topicName: '',
                series: [],
                productService: '',
                theme: '',
                entity: 'IF'
            });
            setPublishProgress({ current: 0, total: 0, currentItem: '' });
            setIsAddingNewTopic(false);
            setCustomTopicName('');
            setIsAddingNewProduct(false);
            setCustomProductName('');
            setIsAddingNewTheme(false);
            setCustomThemeName('');
            setIsAddingNewEntity(false);
            setCustomEntityName('');
        }
    }, [isOpen]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // Handle dropdown changes for fill-in choice fields
        if (name === 'topicName') {
            if (value === 'ADD_NEW_TOPIC') {
                setIsAddingNewTopic(true);
                setFormData(prev => ({ ...prev, [name]: customTopicName }));
            } else {
                setIsAddingNewTopic(false);
                setCustomTopicName('');
                setFormData(prev => ({ ...prev, [name]: value }));
            }
        } else if (name === 'productService') {
            if (value === 'ADD_NEW_PRODUCT') {
                setIsAddingNewProduct(true);
                setFormData(prev => ({ ...prev, [name]: customProductName }));
            } else {
                setIsAddingNewProduct(false);
                setCustomProductName('');
                setFormData(prev => ({ ...prev, [name]: value }));
            }
        } else if (name === 'theme') {
            if (value === 'ADD_NEW_THEME') {
                setIsAddingNewTheme(true);
                setFormData(prev => ({ ...prev, [name]: customThemeName }));
            } else {
                setIsAddingNewTheme(false);
                setCustomThemeName('');
                setFormData(prev => ({ ...prev, [name]: value }));
            }
        } else if (name === 'entity') {
            if (value === 'ADD_NEW_ENTITY') {
                setIsAddingNewEntity(true);
                setFormData(prev => ({ ...prev, [name]: customEntityName }));
            } else {
                setIsAddingNewEntity(false);
                setCustomEntityName('');
                setFormData(prev => ({ ...prev, [name]: value }));
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleCustomTopicChange = (e) => {
        const value = e.target.value;
        setCustomTopicName(value);
        setFormData(prev => ({ ...prev, topicName: value }));
    };

    const handleCustomProductChange = (e) => {
        const value = e.target.value;
        setCustomProductName(value);
        setFormData(prev => ({ ...prev, productService: value }));
    };

    const handleCustomThemeChange = (e) => {
        const value = e.target.value;
        setCustomThemeName(value);
        setFormData(prev => ({ ...prev, theme: value }));
    };

    const handleCustomEntityChange = (e) => {
        const value = e.target.value;
        setCustomEntityName(value);
        setFormData(prev => ({ ...prev, entity: value }));
    };

    const handleSeriesChange = (seriesName, isChecked) => {
        setFormData(prev => {
            if (isChecked) {
                return { ...prev, series: [...prev.series, seriesName] };
            } else {
                return { ...prev, series: prev.series.filter(s => s !== seriesName) };
            }
        });
    };

    const getMediaTypeIcon = (item) => {
        const isVideo = item.mimeType?.startsWith('video/');
        const isAudio = item.mimeType?.startsWith('audio/');
        
        if (isVideo) return faVideo;
        if (isAudio) return faMicrophone;
        return faImage;
    };

    const getMediaType = (item) => {
        const isVideo = item.mimeType?.startsWith('video/');
        const isAudio = item.mimeType?.startsWith('audio/');
        
        if (isVideo) return 'Video';
        if (isAudio) return 'Audio';
        return 'Image';
    };

    const handleBulkPublish = async () => {
        if (!formData.topicName || !formData.productService || !formData.theme || formData.series.length === 0) {
            toast.error('Please fill in all required fields');
            return;
        }

        setIsSubmitting(true);
        setPublishProgress({ current: 0, total: selectedItems.length, currentItem: '' });

        let successCount = 0;
        let errorCount = 0;

        for (let i = 0; i < selectedItems.length; i++) {
            const item = selectedItems[i];
            
            try {
                setPublishProgress({
                    current: i + 1,
                    total: selectedItems.length,
                    currentItem: item.title || item.name
                });

                const mediaData = {
                    topicName: formData.topicName,
                    shortDescription: item.description || `${getMediaType(item)} from gallery`,
                    series: formData.series,
                    productService: formData.productService,
                    theme: formData.theme,
                    entity: formData.entity,
                    webURLLink: item.name, // Store just the filename
                    mediaType: getMediaType(item),
                    originalName: item.name,
                    featured: false,
                    thumbnail: getMediaType(item) === 'Image' ? item.name : '' // Use image as its own thumbnail
                };

                await sendToMediaCentre(mediaData);
                successCount++;

                // Small delay to prevent overwhelming the API
                await new Promise(resolve => setTimeout(resolve, 200));

            } catch (error) {
                console.error(`Failed to publish ${item.name}:`, error);
                errorCount++;
            }
        }

        setIsSubmitting(false);

        if (successCount > 0 && errorCount === 0) {
            toast.success(`🎉 Successfully published ${successCount} items to Media Centre!`);
        } else if (successCount > 0 && errorCount > 0) {
            toast.warning(`Published ${successCount} items successfully, ${errorCount} failed.`);
        } else {
            toast.error('Failed to publish items to Media Centre');
        }

        onSuccess();
    };

    const groupedItems = selectedItems.reduce((acc, item) => {
        const mediaType = getMediaType(item);
        if (!acc[mediaType]) acc[mediaType] = [];
        acc[mediaType].push(item);
        return acc;
    }, {});

    return (
        <Modal show={isOpen} onHide={onClose} size="lg" centered className="modal-bulk-publish">
            <Modal.Header>
                <Modal.Title>
                    <FontAwesomeIcon icon={faCloudArrowUp} className="me-2" />
                    Bulk Publish to Media Centre
                </Modal.Title>
                <Button variant="link" onClick={onClose} className="p-0 border-0" disabled={isSubmitting}>
                    <FontAwesomeIcon icon={faTimes} />
                </Button>
            </Modal.Header>

            <Modal.Body>
                <Alert variant="info" className="mb-4">
                    <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faCloudArrowUp} className="me-2" />
                        <div>
                            <strong>Publishing {selectedItems.length} items:</strong>
                            <div className="mt-2">
                                {Object.entries(groupedItems).map(([type, items]) => (
                                    <span key={type} className="badge me-2" style={{
                                        background: type === 'Video' ? '#764ba2' : 
                                                   type === 'Audio' ? '#20c997' : '#667eea'
                                    }}>
                                        <FontAwesomeIcon icon={
                                            type === 'Video' ? faVideo : 
                                            type === 'Audio' ? faMicrophone : faImage
                                        } className="me-1" />
                                        {items.length} {type}{items.length > 1 ? 's' : ''}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </Alert>

                {isSubmitting && (
                    <Alert variant="warning" className="mb-4">
                        <div className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                            <div className="flex-grow-1">
                                <strong>Publishing in progress...</strong>
                                <div className="mt-2">
                                    <div className="progress">
                                        <div 
                                            className="progress-bar"
                                            role="progressbar"
                                            style={{ width: `${(publishProgress.current / publishProgress.total) * 100}%` }}
                                        >
                                            {publishProgress.current} / {publishProgress.total}
                                        </div>
                                    </div>
                                    <small className="text-muted mt-1">
                                        Currently publishing: {publishProgress.currentItem}
                                    </small>
                                </div>
                            </div>
                        </div>
                    </Alert>
                )}

                <Form>
                    <Row>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Topic Name <span className="text-danger">*</span></Form.Label>
                                {isAddingNewTopic ? (
                                    <div className="d-flex gap-2">
                                        <Form.Control
                                            type="text"
                                            value={customTopicName}
                                            onChange={handleCustomTopicChange}
                                            placeholder="Enter new topic name..."
                                            required
                                            autoFocus
                                            disabled={isSubmitting}
                                        />
                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm"
                                            onClick={() => {
                                                setIsAddingNewTopic(false);
                                                setCustomTopicName('');
                                                setFormData(prev => ({ ...prev, topicName: '' }));
                                            }}
                                            disabled={isSubmitting}
                                        >
                                            ↩️ Back
                                        </Button>
                                    </div>
                                ) : (
                                    <Form.Select
                                        name="topicName"
                                        value={formData.topicName}
                                        onChange={handleInputChange}
                                        required
                                        disabled={isSubmitting}
                                    >
                                        <option value="">Select Topic Name</option>
                                        {choiceFields?.topicNames?.map(topic => (
                                            <option key={topic} value={topic}>{topic}</option>
                                        ))}
                                        <option value="ADD_NEW_TOPIC" style={{ borderTop: '1px solid #ccc', fontStyle: 'italic' }}>
                                            ➕ Add New Topic...
                                        </option>
                                    </Form.Select>
                                )}
                                <Form.Text className="text-muted">
                                    This topic will be applied to all {selectedItems.length} selected items
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Series <span className="text-danger">*</span></Form.Label>
                                <div className="series-selection border rounded p-2" style={{ maxHeight: '120px', overflowY: 'auto' }}>
                                    {choiceFields?.series?.length > 0 ? (
                                        choiceFields.series.map(series => (
                                            <Form.Check
                                                key={series}
                                                type="checkbox"
                                                id={`bulk-series-${series}`}
                                                label={series}
                                                checked={formData.series.includes(series)}
                                                onChange={(e) => handleSeriesChange(series, e.target.checked)}
                                                className="mb-1"
                                                disabled={isSubmitting}
                                            />
                                        ))
                                    ) : (
                                        <div className="text-muted">No series found</div>
                                    )}
                                </div>
                                <Form.Text className="text-muted">
                                    {formData.series.length > 0 
                                        ? `Selected: ${formData.series.join(', ')}` 
                                        : 'Select one or more series (required)'
                                    }
                                </Form.Text>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Product/Service <span className="text-danger">*</span></Form.Label>
                                {isAddingNewProduct ? (
                                    <div className="d-flex gap-2">
                                        <Form.Control
                                            type="text"
                                            value={customProductName}
                                            onChange={handleCustomProductChange}
                                            placeholder="Enter new product/service name..."
                                            required
                                            autoFocus
                                            disabled={isSubmitting}
                                        />
                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm"
                                            onClick={() => {
                                                setIsAddingNewProduct(false);
                                                setCustomProductName('');
                                                setFormData(prev => ({ ...prev, productService: '' }));
                                            }}
                                            disabled={isSubmitting}
                                        >
                                            ↩️ Back
                                        </Button>
                                    </div>
                                ) : (
                                    <Form.Select
                                        name="productService"
                                        value={formData.productService}
                                        onChange={handleInputChange}
                                        required
                                        disabled={isSubmitting}
                                    >
                                        <option value="">Select Product/Service</option>
                                        {choiceFields?.products?.map(product => (
                                            <option key={product} value={product}>{product}</option>
                                        ))}
                                        <option value="ADD_NEW_PRODUCT" style={{ borderTop: '1px solid #ccc', fontStyle: 'italic' }}>
                                            ➕ Add New Product/Service...
                                        </option>
                                    </Form.Select>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Theme <span className="text-danger">*</span></Form.Label>
                                {isAddingNewTheme ? (
                                    <div className="d-flex gap-2">
                                        <Form.Control
                                            type="text"
                                            value={customThemeName}
                                            onChange={handleCustomThemeChange}
                                            placeholder="Enter new theme name..."
                                            required
                                            autoFocus
                                            disabled={isSubmitting}
                                        />
                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm"
                                            onClick={() => {
                                                setIsAddingNewTheme(false);
                                                setCustomThemeName('');
                                                setFormData(prev => ({ ...prev, theme: '' }));
                                            }}
                                            disabled={isSubmitting}
                                        >
                                            ↩️ Back
                                        </Button>
                                    </div>
                                ) : (
                                    <Form.Select
                                        name="theme"
                                        value={formData.theme}
                                        onChange={handleInputChange}
                                        required
                                        disabled={isSubmitting}
                                    >
                                        <option value="">Select Theme</option>
                                        {choiceFields?.themes?.map(theme => (
                                            <option key={theme} value={theme}>{theme}</option>
                                        ))}
                                        <option value="ADD_NEW_THEME" style={{ borderTop: '1px solid #ccc', fontStyle: 'italic' }}>
                                            ➕ Add New Theme...
                                        </option>
                                    </Form.Select>
                                )}
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Entity</Form.Label>
                                {isAddingNewEntity ? (
                                    <div className="d-flex gap-2">
                                        <Form.Control
                                            type="text"
                                            value={customEntityName}
                                            onChange={handleCustomEntityChange}
                                            placeholder="Enter new entity name..."
                                            autoFocus
                                            disabled={isSubmitting}
                                        />
                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm"
                                            onClick={() => {
                                                setIsAddingNewEntity(false);
                                                setCustomEntityName('');
                                                setFormData(prev => ({ ...prev, entity: '' }));
                                            }}
                                            disabled={isSubmitting}
                                        >
                                            ↩️ Back
                                        </Button>
                                    </div>
                                ) : (
                                    <Form.Select
                                        name="entity"
                                        value={formData.entity}
                                        onChange={handleInputChange}
                                        disabled={isSubmitting}
                                    >
                                        <option value="">Select Entity</option>
                                        {choiceFields?.entities?.map(entity => (
                                            <option key={entity} value={entity}>{entity}</option>
                                        ))}
                                        <option value="ADD_NEW_ENTITY" style={{ borderTop: '1px solid #ccc', fontStyle: 'italic' }}>
                                            ➕ Add New Entity...
                                        </option>
                                    </Form.Select>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Alert variant="light" className="mt-3">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                        <strong>Note:</strong> All selected items will be published with the same topic name and metadata. 
                   
                    </Alert>
                </Form>
            </Modal.Body>

            <Modal.Footer>
                <div className="d-flex justify-content-between w-100">
                    <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleBulkPublish}
                        disabled={isSubmitting || !formData.topicName || formData.series.length === 0 || !formData.productService || !formData.theme}
                    >
                        {isSubmitting ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                Publishing {publishProgress.current}/{publishProgress.total}...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faCloudArrowUp} className="me-2" />
                                Publish {selectedItems.length} Items
                            </>
                        )}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalBulkPublishToMediaCentre; 